import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import {Sticky} from 'react-sticky';
import moment from 'moment';
import {Link} from 'react-router-dom';
import browser from 'detect-browser';
import {HashLink} from 'react-router-hash-link';

import Loading from '../Loading/Loading';
import BasketBar from '../BasketBar/BasketBar';
import {BasketTotal} from '../BasketBar/BasketTotal';
import {ToggleButton} from '../BasketBar/ToggleButton';
import PaymentIcons from '../../components/PaymentIcons/PaymentIcons';
import {PAYMENT_TYPES, PROMOTION_TYPES} from '../../constants/constant';
import {
  SideBasketStyled,
  Title,
  BasketContent,
  Message,
  CheckoutButton,
  LoadingStyled,
  Content,
  Summary,
  SummaryContent,
  LeftPanel,
  CheckoutButtonSubTitle,
  RestaurantClosed,
  RestaurantClosedTitle,
  RestaurantClosedText,
  CheckYourOrderMessageStyled,
  PaymentIconsWrapper,
  CheckoutButtonText,
  ChangeOrder,
  DineInOrderText,
  DineInStatus,
  NotDineIn,
  NotDineInLink
} from './SideBasket.style';
import ServiceTypeRadioButtons from '../ServiceTypeRadioButtons/ServiceTypeRadioButtons';
import {getNextOpeningTime, checkOpeningTime, convertExceptionalTimeDay} from '../../helpers/date.helper';
import BasketFee from '../BasketBar/BasketFee';
import {BasketFeeContainer} from '../BasketBar/BasketFee.style';
import Promotions from '../Promotions/Promotions';
import {ButtonSpinner} from '../../styles/components/button.style';

export class SideBasket extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isExpanded: false,
      isSubmittingServiceType: false
    };
    this.expandBasket = this.expandBasket.bind(this);
    this.removeDine = this.removeDine.bind(this);
    this.handleSetServiceType = this.handleSetServiceType.bind(this);
  }

  componentWillUnmount() {
    document.body.classList.remove('mobileBasket__open');
  }

  expandBasket() {
    if (this.props.isSticky) {
      return;
    }
    this.setState({isExpanded: !this.state.isExpanded});
    if (typeof document === 'undefined') {
      return;
    }
    if (this.state.isExpanded) {
      document.body.classList.remove('mobileBasket__open');
    } else {
      document.body.classList.add('mobileBasket__open');
    }
  }

  removeDine() {
    this.setState({isExpanded: false});
    this.props.clearTableId();
  }

  handleSetServiceType(serviceTypeValue) {
    this.setState({isSubmittingServiceType: true});
    const {checkoutForm, changeFormValue, updateFulfillment, basketUuid} = this.props;
    let delivery;

    if (checkoutForm) {
      delivery = {
        serviceType: serviceTypeValue.target.value,
        addressLine1: checkoutForm.values.addressLine1,
        addressLine2: checkoutForm.values.addressLine2,
        city: checkoutForm.values.city,
        postcode: checkoutForm.values.postcode
      };
    } else {
      delivery = {serviceType: serviceTypeValue.target.value};
    }

    changeFormValue('checkoutForm', 'serviceType', serviceTypeValue.target.value);

    return updateFulfillment(basketUuid, delivery)
      .then(() => this.setState({isSubmittingServiceType: false}))
      .catch((error) => {
        this.setState({isSubmittingServiceType: false});
        return Promise.reject(error);
      });
  }

  render() {
    const {
      isMenuPage,
      showServiceTypeRadioButtons,
      goToCheckoutPageCallback,
      isLoading,
      basketItems,
      price,
      promotions,
      basketUuid,
      editingMeals,
      loadExistingBasketItem,
      removeBasketItem,
      updateBasketItemQuantity,
      payment,
      showPaymentIcons,
      openingTimes,
      deliverySettings,
      fulfillment,
      restaurantModes,
      promotionMessages,
      isSticky,
      isTemplateLight,
      exceptionalClosingTimes,
      tableId
    } = this.props;

    const openForDelivery = openingTimes && checkOpeningTime(openingTimes.delivery) && restaurantModes.delivery;
    const openForCollection = openingTimes && checkOpeningTime(openingTimes.collection) && restaurantModes.collection;
    const nextOpeningTime = openingTimes && !openForDelivery && !openForCollection && getNextOpeningTime(openingTimes, restaurantModes);
    let renderContent = basketUuid &&
      <BasketBar
        basketUuid={basketUuid}
        items={basketItems}
        editingMeals={editingMeals}
        loadExistingBasketItem={loadExistingBasketItem}
        removeBasketItem={removeBasketItem}
        updateBasketItemQuantity={updateBasketItemQuantity}
        isExpanded={this.state.isExpanded}
        isTemplateLight={isTemplateLight}
        extraSpacing={!!tableId}
      />;

    if (basketItems.length === 0) {
      renderContent = isLoading ? <Loading isLight={true} /> : <Message data-rw="menu-page--side-basket--empty-text"><FormattedMessage id="basket.emptyBasket" /></Message>;
    }

    const minimumDeliveryFeeNotMet = fulfillment && fulfillment.delivery && deliverySettings && deliverySettings.minimumAmount > price.items;

    const renderPriceTotal = price &&
      <BasketTotal
        total={price.total}
        showDeliveryMinimumMessage={minimumDeliveryFeeNotMet}
        minimumAmount={deliverySettings && deliverySettings.minimumAmount}
        isExpanded={this.state.isExpanded}
      />;

    const renderAppliedPromotions = promotions &&
      promotions.map(promotion =>
        promotion.applied && (promotion.type === PROMOTION_TYPES.DISCOUNT || promotion.type === PROMOTION_TYPES.VOUCHER || promotion.type === PROMOTION_TYPES.SURCHARGE) &&
        <BasketFee
          total={promotion.type === PROMOTION_TYPES.SURCHARGE ? promotion.amount: -promotion.amount}
          promotionMessage={promotion.name}
          key={`${promotion.name}${promotion.amount}`}
        />
      );

    const renderOrderFee = price && price.orderFee !== 0 &&
      <BasketFee
        total={price.orderFee}
        messageStringId="basket.orderAdminFee"
      />;
    const renderDeliveryFee = price && price.delivery !== 0 &&
      <BasketFee
        total={price.delivery}
        messageStringId="basket.deliveryFee"
      />;

    const renderSubtotal = price && price.items !== price.total &&
      <BasketFee
        total={price.items}
        messageStringId="basket.subtotal"
      />;

    const renderTotal = price && !isSticky &&
      <BasketFee
        total={price.total}
        messageStringId="basket.total"
      />;

    const renderOtherFees = (
      <BasketFeeContainer isExpanded={this.state.isExpanded}>
        {renderSubtotal}
        {renderAppliedPromotions}
        {renderDeliveryFee}
        {renderOrderFee}
        {renderTotal}
      </BasketFeeContainer>
    );
    const renderOtherFeesDesktop = !this.state.isExpanded && renderOtherFees;
    const renderOtherFeesMobile = this.state.isExpanded && renderOtherFees;

    const renderChangeOrder = !isMenuPage &&
      <ChangeOrder>
        <Link
          to="/menu"
          data-rw="sidebasket--change-order-link"
        >
          <FormattedMessage id={isSticky ? 'basket.changeOrder' : 'basket.changeOrderMobile'} />
        </Link>
      </ChangeOrder>;
    const renderChangeOrderMobile = !isSticky && renderChangeOrder;
    const renderChangeOrderDesktop = isSticky && renderChangeOrder;

    const checkoutButtonSubTitle = () => {
      if (
        restaurantModes.collection && restaurantModes.delivery && !restaurantModes.futureOrders &&
        openForDelivery && !openForCollection ||
        !restaurantModes.collection && restaurantModes.delivery && !restaurantModes.futureOrders &&
        openForDelivery ||
        !restaurantModes.collection && restaurantModes.delivery && restaurantModes.futureOrders &&
        openForDelivery
      ) {
        return 'basket.forDelivery';
      } else if (
        restaurantModes.collection && restaurantModes.delivery && !restaurantModes.futureOrders &&
        !openForDelivery && openForCollection ||
        restaurantModes.collection && !restaurantModes.delivery && !restaurantModes.futureOrders &&
        openForCollection ||
        restaurantModes.collection && !restaurantModes.delivery && restaurantModes.futureOrders &&
        openForCollection
      ) {
        return 'basket.forCollection';
      } else if (restaurantModes.collection && restaurantModes.delivery && restaurantModes.futureOrders &&
        openForDelivery && !openForCollection) {
        return 'basket.forDeliveryAndPreorderCollection';
      } else if (restaurantModes.collection && restaurantModes.delivery && restaurantModes.futureOrders &&
        !openForDelivery && openForCollection) {
        return 'basket.forCollectionAndPreorderDelivery';
      } else if (restaurantModes.collection && restaurantModes.delivery && restaurantModes.futureOrders &&
        openForDelivery && openForCollection) {
        return 'basket.forDeliveryAndCollection';
      } else if (restaurantModes.collection && restaurantModes.delivery && restaurantModes.futureOrders &&
        !openForDelivery && !openForCollection) {
        return 'basket.forPreOrder';
      } else if (restaurantModes.collection && !restaurantModes.delivery && restaurantModes.futureOrders &&
        !openForCollection) {
        return 'basket.forPreorderCollection';
      } else if (!restaurantModes.collection && restaurantModes.delivery && restaurantModes.futureOrders &&
        !openForDelivery) {
        return 'basket.forPreorderDelivery';
      }
    };

    const renderCheckoutButtonSpinner = isLoading && <ButtonSpinner />;

    const renderCheckoutButtonText = !isLoading &&
      <div>
        <CheckoutButtonText>
          <FormattedMessage id="basket.placeOrder" />
        </CheckoutButtonText>
        <CheckoutButtonSubTitle>
          {
            checkoutButtonSubTitle() &&
            <FormattedMessage id={checkoutButtonSubTitle()} />
          }
        </CheckoutButtonSubTitle>
      </div>;

    const renderCheckoutButton = basketItems && basketItems.length > 0 && isMenuPage && (openForDelivery || openForCollection || restaurantModes.futureOrders) &&
      <CheckoutButton
        isLoading={isLoading}
        onClick={goToCheckoutPageCallback}
        data-rw="menu-page--basket--checkout-button"
        disabled={isLoading}
        noTopMargin={showServiceTypeRadioButtons && isSticky && (openForDelivery || openForCollection || restaurantModes.futureOrders) && restaurantModes.collection && restaurantModes.delivery}
      >
        {renderCheckoutButtonSpinner}
        {renderCheckoutButtonText}
      </CheckoutButton>;

    const renderCheckYourOrderMessage = basketItems && basketItems.length > 0 && isMenuPage && (openForDelivery || openForCollection || restaurantModes.futureOrders) &&
      <CheckYourOrderMessageStyled>
        <FormattedMessage id="basket.totalDeliveryNote" />
      </CheckYourOrderMessageStyled>;

    const nextOpeningTimeMoment = moment(nextOpeningTime);
    const opensInSevenDays = moment().add(7, 'days').isAfter(nextOpeningTimeMoment, 'day');
    const showRestaurantClosedMessage = basketItems && basketItems.length > 0 && isMenuPage && !openForDelivery && !openForCollection && !restaurantModes.futureOrders;
    const renderContactUsLink = isTemplateLight ? (
      <HashLink to="/#contactus">
        <FormattedMessage id="basket.weAreClosedOpeningHours" />
      </HashLink>
    ) : (
      <Link to="/contact">
        <FormattedMessage id="basket.weAreClosedOpeningHours" />
      </Link>
    );
    const renderClosingMessage = nextOpeningTime ? (
      <FormattedMessage
        id="basket.weAreClosedL1"
        values={{
          nextOpeningTimeDay:
            <span>{opensInSevenDays ? nextOpeningTimeMoment.format('dddd') : nextOpeningTimeMoment.format('D/MM/YY')}</span>,
          nextOpeningTimeTime:
            <span>{nextOpeningTimeMoment.format('h:mm A')}</span>
        }}
      />
    ) : (
      exceptionalClosingTimes && exceptionalClosingTimes[0] && (
      <FormattedMessage
        id="basket.weAreClosedFromTo"
        values={{
          fromDate: convertExceptionalTimeDay(exceptionalClosingTimes[0].fromDay, true),
          toDate: convertExceptionalTimeDay(exceptionalClosingTimes[0].toDay)
        }}
      />)
    );
    const renderRestaurantClosed = showRestaurantClosedMessage &&
      <RestaurantClosed>
        <RestaurantClosedTitle><FormattedMessage id="basket.weAreClosedTitle" /></RestaurantClosedTitle>
        <RestaurantClosedText>{renderClosingMessage}</RestaurantClosedText>
        <RestaurantClosedText>
          <FormattedMessage
            id="basket.weAreClosedL2"
            values={{link: renderContactUsLink}}
          />
        </RestaurantClosedText>
      </RestaurantClosed>;

    const renderServiceTypeRadioButtons = (
      <ServiceTypeRadioButtons
        setServiceType={this.handleSetServiceType}
        openForDelivery={openForDelivery}
        openForCollection={openForCollection}
        acceptFutureOrders={restaurantModes.futureOrders}
        isValidating={this.state.isSubmittingServiceType}
        restaurantModes={restaurantModes}
        openingTimes={openingTimes}
      />
    );
    const renderServiceTypeRadioButtonsDesktop = !tableId && showServiceTypeRadioButtons && isSticky && (openForDelivery || openForCollection || restaurantModes.futureOrders) && restaurantModes.collection && restaurantModes.delivery &&
      renderServiceTypeRadioButtons;
    const renderServiceTypeRadioButtonsMobile = !tableId && this.state.isExpanded && showServiceTypeRadioButtons && (openForDelivery || openForCollection || restaurantModes.futureOrders) && restaurantModes.collection && restaurantModes.delivery &&
      renderServiceTypeRadioButtons;
    const renderDineInBar = tableId && (
      <DineInStatus>
        <DineInOrderText>
          Dine In Order - Table {tableId}
        </DineInOrderText>
        <NotDineIn onClick={this.removeDine}>Not dining in? <NotDineInLink>Tap here</NotDineInLink></NotDineIn>
      </DineInStatus>
    );
    const renderDineInDesktop = isSticky && !this.state.isExpanded && renderDineInBar;
    const renderDineInMobile = !isSticky && this.state.isExpanded && renderDineInBar;

    const renderSummarySpinner = isLoading && !isMenuPage && isSticky && <LoadingStyled><Loading isLight={true} /></LoadingStyled>;
    const renderSummary = basketItems.length > 0 &&
      <Summary expand={this.state.isExpanded}>
        {renderDineInMobile}
        {renderServiceTypeRadioButtonsMobile}
        {renderOtherFeesMobile}
        <SummaryContent
          restaurantClosed={!restaurantModes.futureOrders && nextOpeningTime}
          onClick={this.expandBasket}
        >
          {renderSummarySpinner}
          <LeftPanel isMenuPage={isMenuPage}>
            {ToggleButton({isArrowDown: this.state.isExpanded})}
            {renderChangeOrderDesktop}
            {renderOtherFeesDesktop}
            {renderPriceTotal}
            {renderChangeOrderMobile}
          </LeftPanel>
          {renderDineInDesktop}
          {renderServiceTypeRadioButtonsDesktop}
          {renderCheckoutButton}
          {renderCheckYourOrderMessage}
          {renderRestaurantClosed}
        </SummaryContent>
        <PaymentIconsWrapper showPaymentIcons={showPaymentIcons}>
          <PaymentIcons
            supportPayPal={payment.types.indexOf(PAYMENT_TYPES.PAYPAL) > -1}
            supportCreditCard={payment.types.indexOf(PAYMENT_TYPES.CREDIT_CARD) > -1}
          />
        </PaymentIconsWrapper>
      </Summary>;

    const renderPromotionMessages = isSticky && !isMenuPage && promotionMessages &&
      <Promotions
        promotions={promotionMessages}
        page="preview"
      />;

    const isStickySidebasket = isSticky && isMenuPage && browser && browser.name !== 'ie';
    const sidebasketContent = (
      <div>
        <Title><FormattedMessage id="basket.yourOrder" /></Title>
        <BasketContent>{renderContent}</BasketContent>
        {renderSummary}
        {renderPromotionMessages}
      </div>
    );
    const stickyContent = ({style}) => <Content style={style}>{sidebasketContent}</Content>;
    const nonStickyContent = <Content>{sidebasketContent}</Content>;
    const renderSidebasketContent = isStickySidebasket ? <Sticky disableCompensation>{stickyContent}</Sticky> : nonStickyContent;

    return (
      <SideBasketStyled
        id="side-basket"
        data-rw="menu-page--side-basket"
        expand={this.state.isExpanded}
        show={basketItems.length > 0}
      >
        {renderSidebasketContent}
      </SideBasketStyled>
    );
  }
}

SideBasket.propTypes = {
  isMenuPage: PropTypes.bool.isRequired,
  showPaymentIcons: PropTypes.bool.isRequired,
  showServiceTypeRadioButtons: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  basketItems: PropTypes.array.isRequired,
  loadExistingBasketItem: PropTypes.func.isRequired,
  removeBasketItem: PropTypes.func.isRequired,
  updateBasketItemQuantity: PropTypes.func.isRequired,
  updateFulfillment: PropTypes.func,
  payment: PropTypes.object.isRequired,
  editingMeals: PropTypes.object,
  price: PropTypes.object,
  promotions: PropTypes.array,
  basketUuid: PropTypes.string,
  goToCheckoutPageCallback: PropTypes.func,
  changeFormValue: PropTypes.func,
  openingTimes: PropTypes.object,
  checkoutForm: PropTypes.object,
  deliverySettings: PropTypes.object,
  fulfillment: PropTypes.object,
  restaurantModes: PropTypes.object,
  promotionMessages: PropTypes.object,
  isSticky: PropTypes.bool.isRequired,
  isTemplateLight: PropTypes.bool.isRequired,
  exceptionalClosingTimes: PropTypes.array,
  tableId: PropTypes.string,
  clearTableId: PropTypes.func
};

SideBasket.defaultProps = {
  showPaymentIcons: true
};

export default SideBasket;



// WEBPACK FOOTER //
// ./components/SideBasket/SideBasket.js